/**
 * Colours and tooltip copy for the Energy & Productivity Cycle Time donut,
 * legend and activity table.
 *
 * Per direction from the backend team and design, the Cycle Time visualisation
 * no longer consolidates the granular backend activities into a fixed set of
 * categories. The raw activity values returned by the API (e.g. "Dig", "Swing
 * Loaded", "Key On – Engine Off") are used directly as the slice / legend /
 * table labels. This module only supplies a stable colour per known activity
 * and the plain-language tooltip copy shown next to each activity, both keyed
 * by the lower-cased activity name, with a neutral fallback for anything the
 * backend sends that is not yet mapped here.
 */
/** Neutral colour used for any activity without an explicit colour mapping. */
export const DEFAULT_ACTIVITY_COLOR = "#B0B0B0";
/**
 * Stable colour per raw backend activity, keyed by the lower-cased activity
 * name. Colours were provided by design; update this map when design supplies
 * new activities or revised colours. Unrecognised activities fall back to
 * {@link DEFAULT_ACTIVITY_COLOR}.
 */
const ACTIVITY_COLORS = {
    charging: "#1AC69E",
    dig: "#8CBAFF",
    dump: "#4D94FF",
    "key on-engine off": "#FF8946",
    miscellaneous: "#F2F2F2",
    "swing empty": "#696969",
    "swing loaded": "#6852BE",
    travel: "#FFA230"
};
/** Resolves the donut/legend colour for a raw activity name (defaults to grey). */
export const colorForActivity = (activity) => {
    const normalized = normalizeActivityKey(activity);
    return ACTIVITY_COLORS[normalized] ?? DEFAULT_ACTIVITY_COLOR;
};
/** Fallback description for any activity without explicit tooltip copy. */
export const DEFAULT_ACTIVITY_DESCRIPTION = "Time the machine spent in this activity during the selected Cycle Time view.";
/**
 * Plain-language explanation of what each raw activity represents, keyed by the
 * lower-cased activity name. Surfaced in the activity table info tooltips.
 * Unrecognised activities fall back to {@link DEFAULT_ACTIVITY_DESCRIPTION}.
 */
const ACTIVITY_DESCRIPTIONS = {
    charging: "Time the machine spent charging its battery.",
    dig: "Time spent digging material.",
    dump: "Time spent dumping loaded material.",
    "key on-engine off": "Time the machine was powered on with the engine off.",
    miscellaneous: "Time the machine spent in miscellaneous activities.",
    "swing empty": "Time spent swinging the machine without a load.",
    "swing loaded": "Time spent swinging the machine with a load.",
    travel: "Time spent travelling or repositioning the machine."
};
/** Resolves the tooltip description for a raw activity name (defaults to a generic message). */
export const descriptionForActivity = (activity) => {
    const normalized = normalizeActivityKey(activity);
    return ACTIVITY_DESCRIPTIONS[normalized] ?? DEFAULT_ACTIVITY_DESCRIPTION;
};
/**
 * Normalises an activity name into a stable lookup key: trims, lower-cases,
 * converts the en dash to a hyphen, and strips whitespace around hyphens so
 * dash variants ("Key On – Engine Off", "Key On - Engine Off", "Key On-Engine
 * Off") all resolve to the same key, then collapses remaining whitespace.
 */
// Regex `replace` (not `replaceAll`) avoids needing a String.prototype.replaceAll polyfill on older browsers.
export const normalizeActivityKey = (activity) => activity
    .trim()
    .toLowerCase()
    .replace(/–/g, "-")
    .replace(/\s*-\s*/g, "-")
    .replace(/\s+/g, " ");
/**
 * Slugifies an activity label for use in `data-test` IDs. Lower-cases, trims,
 * collapses the en dash to a hyphen (so "Key On – Engine Off" and its hyphen
 * variant produce the same ID), replaces runs of whitespace or slashes with a
 * single hyphen, then collapses any repeated hyphens so IDs stay stable across
 * backend label variants (e.g. "Key On – Engine Off" vs "Key On-Engine Off").
 */
export const slugifyLabel = (label) => label
    .trim()
    .toLowerCase()
    .replace(/–/g, "-")
    .replace(/[\s/]+/g, "-")
    .replace(/-+/g, "-");
/**
 * Fixed display order for the donut slices and legend. The legend is rendered
 * as two columns filled down the first column then the second, so the first
 * half of this list is the left column and the second half the right. Any
 * activity not listed here is shown after these, sorted alphabetically.
 */
export const LEGEND_ACTIVITY_ORDER = [
    "Dig",
    "Dump",
    "Travel",
    "Charging",
    "Swing Empty",
    "Swing Loaded",
    "Key On – Engine Off",
    "Miscellaneous"
];
const LEGEND_RANK = new Map(LEGEND_ACTIVITY_ORDER.map((label, index) => [normalizeActivityKey(label), index]));
/** Alphabetical, case-insensitive comparison of two activity labels. */
const compareAlphabetical = (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" });
/**
 * Comparator ordering activities by {@link LEGEND_ACTIVITY_ORDER}, with any
 * activity not in that list sorted alphabetically after the known ones. Used
 * for the donut slices and the legend.
 */
export const compareLegendOrder = (a, b) => {
    const rankA = LEGEND_RANK.get(normalizeActivityKey(a)) ?? Number.POSITIVE_INFINITY;
    const rankB = LEGEND_RANK.get(normalizeActivityKey(b)) ?? Number.POSITIVE_INFINITY;
    if (rankA !== rankB)
        return rankA - rankB;
    return compareAlphabetical(a, b);
};
/**
 * Fixed display order for the Activities table, matching the design. Any
 * activity not listed here is shown after these, sorted alphabetically.
 */
export const TABLE_ACTIVITY_ORDER = [
    "Swing Loaded",
    "Dig",
    "Dump",
    "Charging",
    "Key On – Engine Off",
    "Miscellaneous",
    "Travel",
    "Swing Empty"
];
const TABLE_RANK = new Map(TABLE_ACTIVITY_ORDER.map((label, index) => [normalizeActivityKey(label), index]));
/**
 * Comparator ordering the Activities table by {@link TABLE_ACTIVITY_ORDER},
 * with any activity not in that list sorted alphabetically after the known ones.
 */
export const compareTableOrder = (a, b) => {
    const rankA = TABLE_RANK.get(normalizeActivityKey(a)) ?? Number.POSITIVE_INFINITY;
    const rankB = TABLE_RANK.get(normalizeActivityKey(b)) ?? Number.POSITIVE_INFINITY;
    if (rankA !== rankB)
        return rankA - rankB;
    return compareAlphabetical(a, b);
};
